"use client";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ButtonLink } from "../primitives/Button";
import { Container } from "../primitives/Container";
import type { BrandLike, NavItem } from "./types";

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href);
}
function isBranchActive(pathname: string, item: NavItem) {
  if (item.href && isActive(pathname, item.href)) return true;
  return (item.children ?? []).some((c) => isActive(pathname, c.href));
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 12 8" aria-hidden="true" className={`size-2.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M1 1.5 6 6.5l5-5" />
    </svg>
  );
}

/** Desktop drop-down. Items are plain links — no descriptions, per CEO feedback. */
function NavMenu({ item, pathname }: { item: NavItem; pathname: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLLIElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedByHover = useRef(false);

  const cancel = useCallback(() => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } }, []);
  const scheduleClose = useCallback(() => { cancel(); timer.current = setTimeout(() => setOpen(false), 180); }, [cancel]);
  useEffect(() => cancel, [cancel]);
  useEffect(() => { if (!open) openedByHover.current = false; }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); trigger.current?.focus(); } };
    const onOutside = (e: Event) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("focusin", onOutside);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onOutside); document.removeEventListener("focusin", onOutside); };
  }, [open]);

  const active = isBranchActive(pathname, item);
  const children = item.children ?? [];
  const cols = children.length > 4 ? "grid-cols-2" : "grid-cols-1";

  return (
    <li ref={ref} className="relative" onMouseEnter={() => { cancel(); setOpen((o) => { if (!o) openedByHover.current = true; return true; }); }} onMouseLeave={scheduleClose}>
      <button ref={trigger} type="button" aria-expanded={open} aria-controls={id}
        onClick={() => { if (openedByHover.current) { openedByHover.current = false; setOpen(true); return; } setOpen((o) => !o); }}
        className={`flex items-center gap-1.5 rounded-pill px-4 py-2 font-sans text-sm font-medium transition-colors ${active ? "text-accent-ink" : "text-slate hover:text-ink"}`}>
        {item.label}<Chevron open={open} />
      </button>
      <div id={id} hidden={!open} className="absolute left-1/2 top-full z-10 -translate-x-1/2 pt-3">
        <div className="overflow-hidden rounded-card border border-hairline bg-paper-raised shadow-2xl">
          <ul className={`grid ${cols} gap-0.5 p-2 ${children.length > 4 ? "w-[30rem]" : "w-[16rem]"}`}>
            {children.map((c) => {
              const cur = isActive(pathname, c.href);
              return (
                <li key={c.href}>
                  <Link href={c.href} aria-current={cur ? "page" : undefined} onClick={() => setOpen(false)}
                    className={`block rounded-lg px-3 py-2.5 font-sans text-sm font-medium transition-colors ${cur ? "bg-mist text-accent-ink" : "text-ink hover:bg-mist"}`}>
                    {c.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </li>
  );
}

function MobileGroup({ item, pathname, onNavigate }: { item: NavItem; pathname: string; onNavigate: () => void }) {
  const active = isBranchActive(pathname, item);
  const [open, setOpen] = useState(active);
  const id = useId();
  return (
    <li>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between rounded-lg px-4 py-3 font-sans text-base font-medium ${active ? "bg-mist text-accent-ink" : "text-slate-strong hover:bg-mist"}`}>
        {item.label}<Chevron open={open} />
      </button>
      <ul id={id} hidden={!open} className="mb-1 ml-4 space-y-0.5 border-l border-hairline pl-3">
        {(item.children ?? []).map((c) => (
          <li key={c.href}>
            <Link href={c.href} onClick={onNavigate} aria-current={isActive(pathname, c.href) ? "page" : undefined}
              className={`block rounded-lg px-4 py-2.5 font-sans text-sm ${isActive(pathname, c.href) ? "bg-mist text-accent-ink" : "text-slate hover:bg-mist"}`}>
              {c.label}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

export function SiteHeader({ brand, nav }: { brand: BrandLike; nav: NavItem[] }) {
  const pathname = usePathname() ?? "/";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); toggle.current?.focus(); } };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open]);

  const links = nav.filter((n) => !n.cta);
  const ctas = nav.filter((n) => n.cta && n.href);

  return (
    <header data-elevated={scrolled || open} className={`sticky top-[var(--frame-gap,0px)] z-50 transition-all duration-300 ease-out-soft ${scrolled || open ? "bg-paper/92 shadow-[0_1px_0_0_var(--color-hairline)] backdrop-blur-md" : "bg-paper"}`}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-pill focus:bg-ink focus:px-4 focus:py-2 focus:text-paper-raised">Skip to content</a>
      <Container>
        <div className="flex h-20 items-center justify-between gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr]">
          <Link href="/" className="flex shrink-0 items-center" aria-label={`${brand.name} — home`}>
            <Image src={brand.logo.src} alt={brand.logo.alt} width={brand.logo.width} height={brand.logo.height} priority className={`${brand.logo.headerClass ?? "h-8"} w-auto`} />
          </Link>
          <nav aria-label="Primary" className="hidden lg:block lg:justify-self-center">
            <ul className="flex items-center gap-1">
              {links.map((item) =>
                item.children?.length ? <NavMenu key={item.label} item={item} pathname={pathname} /> : item.href ? (
                  <li key={item.href}>
                    <Link href={item.href} aria-current={isActive(pathname, item.href) ? "page" : undefined}
                      className={`rounded-pill px-4 py-2 font-sans text-sm font-medium transition-colors ${isActive(pathname, item.href) ? "text-accent-ink" : "text-slate hover:text-ink"}`}>
                      {item.label}
                    </Link>
                  </li>
                ) : null,
              )}
            </ul>
          </nav>
          <div className="hidden shrink-0 items-center gap-3 lg:flex lg:justify-self-end">
            {ctas.map((c, i) => (
              <ButtonLink key={c.href} href={c.href!} style={i === ctas.length - 1 ? "primary" : "ghost"}>{c.label}</ButtonLink>
            ))}
          </div>
          <button ref={toggle} type="button" aria-expanded={open} aria-controls="mobile-nav" onClick={() => setOpen((o) => !o)}
            className="-mr-2 flex size-11 items-center justify-center rounded-lg text-ink lg:hidden">
            <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
            <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              {open ? <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" /> : <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </Container>
      <div id="mobile-nav" hidden={!open} className="max-h-[calc(100dvh-5rem)] overflow-y-auto border-t border-hairline bg-paper lg:hidden">
        <Container>
          <nav aria-label="Primary (mobile)" className="py-6">
            <ul className="space-y-1">
              {links.map((item) =>
                item.children?.length ? <MobileGroup key={item.label} item={item} pathname={pathname} onNavigate={() => setOpen(false)} /> : item.href ? (
                  <li key={item.href}>
                    <Link href={item.href} className={`block rounded-lg px-4 py-3 font-sans text-base font-medium ${isActive(pathname, item.href) ? "bg-mist text-accent-ink" : "text-slate-strong hover:bg-mist"}`}>{item.label}</Link>
                  </li>
                ) : null,
              )}
            </ul>
            {ctas.length ? <div className="mt-5 flex flex-col gap-3">{ctas.map((c) => <ButtonLink key={c.href} href={c.href!} className="w-full">{c.label}</ButtonLink>)}</div> : null}
          </nav>
        </Container>
      </div>
    </header>
  );
}
